// eslint-disable-next-line no-restricted-imports
import { useEffect, useMemo, useState } from 'react';
import { AddNewStudentModal } from '@applyboard/ab-rpj';
import { Combobox, useToasts } from '@applyboard/crystal-ui';
import { RawStudent, useGetStudents } from '../../data/students';

const ADD_STUDENT = 'add-student';

export interface StudentDetails {
  studentId: string;
  studentFirstName: string;
  studentLastName: string;
  isNewStudent: boolean;
}

interface StudentSearchComboboxProps {
  onChange?: (student: StudentDetails) => void;
}

function StudentSearchCombobox({ onChange }: StudentSearchComboboxProps) {
  const toast = useToasts();
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [studentQuery, setStudentQuery] = useState('');
  const [newStudentId, setNewStudentId] = useState('');
  const [debouncedStudentQuery, setDebouncedStudentQuery] = useState('');
  const canQueryStudent = debouncedStudentQuery.length >= 3;
  const [student, setStudent] = useState('');
  const { students, studentsError, isLoadingStudents } = useGetStudents(
    {
      orFilter: {
        studentId: newStudentId,
        studentNameFilter: debouncedStudentQuery,
      },
    },
    { enabled: canQueryStudent },
  );
  const studentMap: Record<string, RawStudent> = useMemo(() => {
    return (
      students?.reduce(
        (acc, student) => {
          acc[student.id] = student;
          return acc;
        },
        {} as Record<string, RawStudent>,
      ) ?? {}
    );
  }, [students]);

  useEffect(() => {
    if (studentsError) {
      console.error('Failed to get the list of student, please try again later', studentsError);
      toast.negative(new Error('Failed to get the list of student, please try again later'));
    }
  }, [studentsError, toast]);

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedStudentQuery(studentQuery), 600);
    return () => clearTimeout(timerId);
  }, [studentQuery]);

  useEffect(() => {
    if (newStudentId && studentMap[newStudentId]) {
      setStudent(newStudentId);
      // Clear the student query and use id
      setStudentQuery('');

      const studentDetails = studentMap[newStudentId];
      onChange?.({
        studentId: newStudentId,
        studentFirstName: studentDetails.attributes.first_name,
        studentLastName: studentDetails.attributes.last_name,
        isNewStudent: true,
      });
    }
  }, [newStudentId, studentMap, onChange]);

  const onCreateStudent = (studentData: { id: string }) => setNewStudentId(studentData.id);

  return (
    <>
      <Combobox
        aria-label="student-search-combobox"
        type="search"
        onSearch={setStudentQuery}
        onChange={(studentId: string) => {
          setNewStudentId('');

          if (studentId === ADD_STUDENT) {
            setShowAddStudent(true);
            setStudentQuery('');
            return;
          }

          const studentDetails = studentMap[studentId];
          if (studentDetails) {
            setStudent(studentId);
            onChange?.({
              studentId: studentId,
              studentFirstName: studentDetails.attributes?.first_name,
              studentLastName: studentDetails.attributes?.last_name,
              isNewStudent: false,
            });
          }
        }}
        loading={isLoadingStudents}
        placeholder="Start typing to search for a student"
        filterOption={() => true}
        value={student}
      >
        <Combobox.Option key="add" label="Add Student" value={ADD_STUDENT} />
        {students?.map(student => (
          <Combobox.Option
            key={student.id}
            label={`${student.attributes.first_name} ${student.attributes.last_name}`}
            value={student.id}
          />
        ))}
      </Combobox>
      {showAddStudent && (
        <AddNewStudentModal
          open={showAddStudent}
          setShowModal={setShowAddStudent}
          onCreatedStudent={onCreateStudent}
        />
      )}
    </>
  );
}

export default StudentSearchCombobox;
